import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import { Stack, SvgIconProps, Tooltip, Typography } from "@mui/material";
import { ComponentType } from "react";
import { prettyPrintWasteClassification } from "routes/Customer/Waste/Facilities/Facility/prettyPrintWasteClassification";
import { SectionCard } from "routes/Customer/Waste/Manifests/sections/SectionCard";
import { SectionHeader } from "routes/Customer/Waste/Manifests/sections/SectionHeader";

import { DifferenceChip } from "./DifferenceChip";
import {
  differenceNote,
  DifferenceRow,
  Differences,
  universalWasteCategoryValue,
} from "./differences";
import { reportabilityValue, wasteKindConclusionLabel } from "./explainedFields";
import { WasteClassificationExplanation } from "./types";

/**
 * Mirrors `SummarySection`'s `LEVEL_ICON` map (not exported there) for the
 * two levels a verdict row needs: a plain info marker normally, a warning
 * marker when the line carries something the rules don't produce.
 */
const CONCLUSION_ICON: Record<
  "info" | "warning",
  { Icon: ComponentType<SvgIconProps>; color: string }
> = {
  info: { Icon: InfoIcon, color: "info.main" },
  warning: { Icon: ErrorIcon, color: "warning.main" },
};

/**
 * One verdict: the question in bold, the engine's answer plain beside it, and
 * an icon bullet that turns from info to warning when the line disagrees. The
 * warning carries a tooltip naming both answers; the info marker has nothing
 * to explain, so it carries none.
 */
function ConclusionRow({
  label,
  value,
  details = [],
  difference,
}: {
  label: string;
  value: string;
  details?: readonly string[];
  difference?: DifferenceRow;
}) {
  const note = differenceNote(difference);
  const { Icon, color } = CONCLUSION_ICON[note === null ? "info" : "warning"];
  const marker = (
    <Icon
      sx={{ fontSize: "1.125rem", color, flexShrink: 0, mt: "2px" }}
      aria-label={note === null ? undefined : "Warning"}
    />
  );

  return (
    <Stack component="li" direction="row" gap={1} alignItems="flex-start">
      {note === null ? marker : <Tooltip title={note}>{marker}</Tooltip>}
      <Stack gap={0.25} sx={{ minWidth: 0 }}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          <Typography variant="body2">
            <Typography component="span" variant="body2" fontWeight={600}>
              {label}:
            </Typography>{" "}
            {value}
          </Typography>
          {difference && <DifferenceChip row={difference} />}
        </Stack>
        {details.map((detail) => (
          <Typography key={detail} variant="body2" color="text.secondary">
            {detail}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}

/**
 * What the compliance engine concludes about this line, stated as verdicts
 * rather than left for a reader to infer from the rule sections below. An
 * icon-bulleted list, the same shape `SummarySection` uses for a manifest's
 * at-a-glance health, so the two read as the same kind of card.
 *
 * These are the rules' answers, never a value someone typed: the engine strips
 * manual and vendor values before deriving. Where the line carries something
 * else, the verdict's marker turns to a warning and a chip carries the line's
 * own value.
 */
export function ClassificationConclusions({
  explanation,
  differences,
}: {
  explanation: WasteClassificationExplanation;
  differences: Differences;
}) {
  const { result } = explanation;
  const hasDifferences = Object.values(differences).some(Boolean);

  return (
    <SectionCard>
      <SectionHeader title="What Encamp decided" />
      <Stack gap={1.5}>
        {hasDifferences && (
          <Typography variant="body2" color="text.secondary">
            This line carries values the rules don't produce today. Each is chipped beside the
            verdict it stands in for.
          </Typography>
        )}
        {/* A list of verdicts, marked up as one: each row is a peer, in a fixed order. */}
        <Stack component="ul" gap={1.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
          <ConclusionRow
            label="Classification"
            value={prettyPrintWasteClassification(result.classification)}
            difference={differences.classification}
          />
          <ConclusionRow
            label="Waste kind"
            value={wasteKindConclusionLabel(result.wasteKind)}
            difference={differences.wasteKind}
          />
          {(result.universalWasteCategory || differences.universalWasteGrant) && (
            <ConclusionRow
              label="Universal waste category"
              value={universalWasteCategoryValue(result.universalWasteCategory)}
              difference={differences.universalWasteGrant}
            />
          )}
          {/* The biennial and e-Manifest answers sit under the verdict they add up to, one per line. */}
          <ConclusionRow
            label="Reportability"
            value={reportabilityValue(result)}
            details={[
              `Federal biennial report: ${result.reportableOnFederalBiennial ? "Reportable" : "Not reportable"}`,
              `Subject to e-Manifest: ${result.subjectToEManifest ? "Yes" : "No"}`,
            ]}
            difference={differences.reportableOnFederalBiennial}
          />
        </Stack>
      </Stack>
    </SectionCard>
  );
}
